import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, Select, Button, Tooltip, theme, Progress, Input, DatePicker, Space, Checkbox, message, Row, Col } from '../../ui';
import type { TagTone } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Download, FileText, Wallet, AlertCircle, Clock } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request, api } from '../../api/client';
import { usePermission, useAuthStore } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { useDebounce } from '../../hooks/useDebounce';
import { displayMobile } from '../../lib/validators';
import { PaymentFormModal } from './PaymentFormModal';

const STATUS_COLOR: Record<string, TagTone> = {
  OPEN: 'blue', PARTIAL: 'gold', PAID: 'green', OVERDUE: 'red', CANCELLED: 'neutral',
};
const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const canRecordPayment = usePermission('PAYMENT.CREATE');

  // Open the invoice PDF: the uploaded Tally PDF if present (legacy), else the
  // in-app generated tax-invoice PDF rendered on demand.
  const openInvoicePdf = async (r: any) => {
    if (r.pdfUrl) { window.open(r.pdfUrl, '_blank', 'noopener'); return; }
    try {
      const res = await api.get(`/invoices/${r.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      message.error('Failed to load invoice PDF');
    }
  };
  const [status, setStatus] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [retailerId, setRetailerId] = useState<string | undefined>();
  // The RangePicker emits [start, null] on the first click (before the end date is picked), so hold
  // nullable endpoints and only query once BOTH are set — else range[1].format() crashes the page.
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [balance, setBalance] = useState<string | undefined>();
  const [overdue, setOverdue] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (retailerId) p.retailerId = retailerId;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    if (balance) p.balance = balance;
    if (overdue) p.overdue = 'true';
    return p;
  }, [status, search, retailerId, range, balance, overdue]);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', params],
    queryFn: () => request<any[]>({ url: '/invoices', params }),
    placeholderData: keepPreviousData,
  });

  // Invoice balances are GROSS — they don't reflect a sales-return credit note, which posts to the retailer
  // ledger as advance-in-credit (so the invoices page would otherwise show a larger "outstanding" than the
  // account statement). On the retailer's own (unfiltered) view we fold their credit-in-hand into the open
  // invoices — oldest first — so the rows, KPIs and My Account all show the SAME net figures.
  const round2 = (n: number) => Math.round((n || 0) * 100) / 100;
  const role = useAuthStore((s) => s.user?.role);
  const isRetailerView = role === 'RETAILER';
  const unfiltered = !status && !search.trim() && !retailerId && !range && !balance && !overdue;
  const { data: meRetailer } = useQuery({
    queryKey: ['invoices-me-retailer'],
    queryFn: () => request<any>({ url: '/retailers/me' }),
    enabled: isRetailerView,
  });
  const netOwed = Math.max(0, Number(meRetailer?.outstandingBalance) || 0);
  const grossOutstanding = useMemo(
    () => (data ?? []).reduce((s, i: any) => s + (i.status !== 'CANCELLED' && Number(i.balanceAmt) > 0 ? Number(i.balanceAmt) : 0), 0),
    [data],
  );
  // Credit that isn't reflected in any invoice balance = gross invoice outstanding − net ledger owed.
  const applyCredit = isRetailerView && unfiltered;
  const creditInHand = applyCredit ? Math.max(0, round2(grossOutstanding - netOwed)) : 0;

  // Each row carries a netBalance (after credit) + creditApplied so the table, KPIs and account reconcile.
  const displayRows = useMemo(() => {
    const rows = (data ?? []).map((r: any) => ({ ...r, creditApplied: 0, netBalance: round2(Number(r.balanceAmt) || 0) }));
    if (creditInHand <= 0.009) return rows;
    let remaining = creditInHand;
    const open = rows.filter((r: any) => r.status !== 'CANCELLED' && r.netBalance > 0)
      .sort((a: any, b: any) => String(a.invoiceDate).localeCompare(String(b.invoiceDate)) || (Number(a.id) - Number(b.id)));
    for (const r of open) {
      if (remaining <= 0.009) break;
      const applied = Math.min(remaining, r.netBalance);
      r.creditApplied = round2(applied);
      r.netBalance = round2(r.netBalance - applied);
      remaining = round2(remaining - applied);
    }
    return rows;
  }, [data, creditInHand]);

  const kpis = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let invoiced = 0, cashPaid = 0, outstanding = 0, overdueAmt = 0, openCount = 0, overdueCount = 0;
    for (const i of displayRows) {
      // A voided invoice (CANCELLED) is not live invoiced revenue — its ledger debit was reversed on
      // void — so exclude it from Invoiced/Collected/count too, not just from outstanding/overdue.
      if (i.status === 'CANCELLED') continue;
      invoiced += Number(i.grandTotal || 0);
      cashPaid += Number(i.paidAmt || 0);
      const bal = Number(i.netBalance || 0);
      if (bal > 0.009) {
        outstanding += bal; openCount++;
        if (i.dueDate && new Date(i.dueDate) < today) { overdueAmt += bal; overdueCount++; }
      }
    }
    invoiced = round2(invoiced); outstanding = round2(outstanding);
    // Retailer view: "settled" = payments + return credits (matches the statement's "Paid / credited"); for
    // staff it stays cash collected. invoiced − outstanding folds in the credit exactly.
    const settled = applyCredit ? round2(invoiced - outstanding) : round2(cashPaid);
    const count = displayRows.filter((i: any) => i.status !== 'CANCELLED').length;
    return { count, invoiced, settled, outstanding, overdueAmt: round2(overdueAmt), openCount, overdueCount };
  }, [displayRows, applyCredit]);

  // Resolve retailerId -> retailer details for the table + the retailer filter (gated).
  const { data: retailers } = useQuery({
    // Wide fetch so most invoices resolve to a name; full coverage for very large
    // retailer bases ultimately wants the invoice payload enriched server-side.
    queryKey: ['invoice-retailer-map'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 1000 } }),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailers]);

  const columns: DataTableColumn<any>[] = [
    { key: 'invoiceNo', title: 'Invoice No', dataIndex: 'invoiceNo', sortable: true, alwaysVisible: true },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true,
      render: (_: any, r: any) => {
        // Prefer the name enriched on the invoice payload (always present); fall back
        // to the client-side retailer map, then the bare id.
        const ret = retailerMap.get(String(r.retailerId));
        const name = r.retailerName ?? ret?.companyName;
        const code = r.retailerCode ?? ret?.code;
        const city = r.retailerCity ?? ret?.city;
        const mobile = r.retailerMobile ?? ret?.mobile;
        return name ? (
          <div>
            <div style={{ fontWeight: 600 }}>{name}</div>
            <div style={{ fontSize: 12, color: token.colorTextTertiary }}>
              {code}{city ? ` · ${city}` : ''}{mobile ? ` · ${displayMobile(mobile)}` : ''}
            </div>
          </div>
        ) : <span style={{ color: token.colorTextTertiary }}>#{r.retailerId}</span>;
      },
    },
    { key: 'invoiceDate', title: 'Date', dataIndex: 'invoiceDate', sortable: true },
    { key: 'dueDate', title: 'Due', dataIndex: 'dueDate', sortable: true },
    {
      key: 'grandTotal', title: 'Grand Total', dataIndex: 'grandTotal',
      align: 'right', sortable: true, render: (v: any) => inr2(v),
    },
    { key: 'paidAmt', title: 'Paid', dataIndex: 'paidAmt', align: 'right', sortable: true, render: (v: any) => inr2(v) },
    {
      key: 'balanceAmt', title: 'Balance', dataIndex: 'netBalance',
      align: 'right', sortable: true,
      // netBalance = invoice balance after applying the retailer's return credit-in-hand (retailer view); for
      // staff it equals the gross balance. A credited row shows how much credit offset it.
      render: (v: any, r: any) => (
        <div>
          <b style={{ color: Number(v) > 0 ? '#D9363E' : '#52C41A' }}>{inr2(v)}</b>
          {Number(r.creditApplied) > 0 && <div style={{ fontSize: 11, color: '#1F8A4C' }}>−{inr2(r.creditApplied)} credit</div>}
        </div>
      ),
    },
    {
      key: 'progress', title: 'Payment', width: 170, alwaysVisible: true,
      render: (_: any, r: any) => {
        const total = Number(r.grandTotal) || 0;
        const paid = Number(r.paidAmt) || 0;
        const credit = Number(r.creditApplied) || 0;
        const covered = paid + credit; // cash + return credit applied
        const pct = total > 0 ? Math.min(100, Math.round((covered / total) * 100)) : 0;
        const strokeColor = pct >= 100 ? '#52C41A' : pct > 0 ? '#E7B22C' : '#D9363E';
        return (
          <Tooltip label={`Paid ${inr2(paid)}${credit > 0 ? ` + credit ${inr2(credit)}` : ''} of ${inr2(total)} · balance ${inr2(r.netBalance)}`}>
            <Progress percent={pct} size="small" strokeColor={strokeColor} />
          </Tooltip>
        );
      },
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (s: string) => <Tag tone={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      key: 'pdf', title: 'PDF', width: 70, align: 'center', alwaysVisible: true,
      render: (_: any, r: any) => (
        <Tooltip label="Download invoice PDF">
          <Button
            variant="text"
            aria-label="Download invoice PDF"
            icon={<Download size={16} />}
            onClick={(e) => { e.stopPropagation(); openInvoicePdf(r); }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Invoices</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Space wrap>
            {canRecordPayment ? (
              <Button variant="primary" size="sm" icon={<DollarSign size={16} />} onClick={() => setPaymentOpen(true)}>
                Record Payment
              </Button>
            ) : (
              <Tooltip label="You don't have permission to record payments (requires PAYMENT.CREATE)">
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                  <Button variant="primary" size="sm" icon={<DollarSign size={16} />} disabled style={{ pointerEvents: 'none' }}>
                    Record Payment
                  </Button>
                </span>
              </Tooltip>
            )}
          </Space>
        </div>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Kpi title="Invoiced" value={inr(kpis.invoiced)} sub={`${kpis.count} invoice${kpis.count === 1 ? '' : 's'}`} color="#1677FF" icon={<FileText size={16} />} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi title={applyCredit ? 'Paid / credited' : 'Collected'} value={inr(kpis.settled)} sub={applyCredit ? 'payments + return credits' : 'payments received'} color="#1F8A4C" icon={<Wallet size={16} />} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi title="Outstanding" value={inr(kpis.outstanding)} sub={applyCredit && creditInHand > 0.01 ? 'net of return credit' : `${kpis.openCount} open`} color="#E7B22C" icon={<AlertCircle size={16} />} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi title="Overdue" value={inr(kpis.overdueAmt)} sub={`${kpis.overdueCount} past due`} color="#D9363E" icon={<Clock size={16} />} />
        </Col>
      </Row>

      {/* Note the return credit folded into the figures, so the retailer sees why invoices net down to what
          their account statement shows. */}
      {applyCredit && creditInHand > 0.01 && (
        <div className="mb-3 rounded-card border border-success-500/25 bg-success-500/10 px-4 py-2.5 text-[13px] text-content">
          <span className="font-semibold text-success-600">{inr2(creditInHand)} return credit</span> applied against your open invoices — the figures below are net of it and match your <span className="font-semibold">account statement</span>.
        </div>
      )}

      <Card>
        <DataTable
          storageKey="invoices-list"
          rowKey="id"
          loading={isLoading}
          dataSource={displayRows}
          columns={columns}
          onRow={(i: any) => ({
            onClick: () => navigate(`/billing/invoices/${i.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Invoice no" style={{ width: 150 }}
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <Select placeholder="Status" allowClear style={{ width: 140 }} value={status} onChange={setStatus}
                options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: s }))} />
              {canViewRetailers && (
                <Select placeholder="Retailer" allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
                  value={retailerId} onChange={setRetailerId}
                  options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.city ? ` · ${r.city}` : ''}` }))} />
              )}
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r)} />
              <Select placeholder="Balance" allowClear style={{ width: 150 }} value={balance} onChange={setBalance}
                options={[{ value: 'OUTSTANDING', label: 'Has balance' }, { value: 'PAID', label: 'Fully paid' }]} />
              <Checkbox checked={overdue} onCheckedChange={(checked) => setOverdue(checked)} label="Overdue only" />
            </Space>
          }
        />
      </Card>
      <PaymentFormModal open={paymentOpen} onClose={() => setPaymentOpen(false)} />
    </div>
  );
}

function Kpi({ title, value, sub, color, icon }: { title: string; value: React.ReactNode; sub?: string; color: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-surface shadow-card" style={{ borderLeft: `3px solid ${color}` }}>
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color, fontSize: 18 }}>{icon}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{title}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color, whiteSpace: 'nowrap' }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: '#aaa' }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
